import { defineStore } from "pinia"

import NotificationDataService from "@/services/NotificationDataService"
import { matchesNotificationCenterTypeFilter } from "@/static/NotificationCenter"
import { useAccountStore } from "@/stores/account"
import { useAuthenticationStore } from "@/stores/authentication"
import { useSpaceStore } from "@/stores/space"
import type {
  INotificationCenterFilters,
  INotificationCenterItem,
  NotificationCenterTypeFilter,
} from "@/types/NotificationCenter.types"
import {
  getNotificationCenterItemCreatedAt,
  isNotificationCenterAppNotification,
  isNotificationCenterSolicitation,
} from "@/types/NotificationCenter.types"
import type { INotification } from "@/types/Notifications.types"
import {
  fetchSolicitationCenterItems,
  mapAppNotificationsToCenterItems,
} from "@/utils/notificationCenterMappers"

const defaultFilters = (): INotificationCenterFilters => ({
  type: "all",
  state: "all",
  establishmentId: null,
})

function matchesTypeFilter(item: INotificationCenterItem, type: NotificationCenterTypeFilter): boolean {
  if (type === "all") {
    return true
  }

  if (type === "solicitations") {
    return isNotificationCenterSolicitation(item)
  }

  if (!isNotificationCenterAppNotification(item)) {
    return false
  }

  return matchesNotificationCenterTypeFilter(item.notificationType, type)
}

function sortNotificationCenterItems(a: INotificationCenterItem, b: INotificationCenterItem): number {
  return (
    new Date(getNotificationCenterItemCreatedAt(b)).getTime() -
    new Date(getNotificationCenterItemCreatedAt(a)).getTime()
  )
}

function buildEstablishmentNameMap(): Map<number, string> {
  const accountStore = useAccountStore()
  return new Map(accountStore.establishments.map((establishment) => [establishment.id, establishment.name]))
}

async function ensureEstablishmentsLoaded(force = false): Promise<void> {
  const accountStore = useAccountStore()

  if (!force && accountStore.establishments.length > 0) {
    return
  }

  await new Promise<void>((resolve) => {
    accountStore.getEstablishments(() => resolve())
  })
}

async function ensureDestinationProvidersLoaded(force = false): Promise<void> {
  const accountStore = useAccountStore()
  const spaceStore = useSpaceStore()

  if (!accountStore.hasDestinationProviders) {
    return
  }

  if (!force && spaceStore.destinationProviders.length > 0) {
    return
  }

  await spaceStore.getDestinationProviders()
}

export const useNotificationCenterStore = defineStore("notificationCenter", {
  state: () => ({
    items: [] as INotificationCenterItem[],
    filters: defaultFilters(),
    loaded: false,
    loading: false,
  }),

  getters: {
    visibleItems(state): INotificationCenterItem[] {
      return state.items.filter((item) => !item.archived)
    },

    filteredItems(): INotificationCenterItem[] {
      const { type, state, establishmentId } = this.filters

      return this.visibleItems
        .filter((item) => {
          if (!matchesTypeFilter(item, type)) {
            return false
          }
          if (establishmentId !== null && item.establishmentId !== establishmentId) {
            return false
          }
          // Lu / non-lu ne s'applique qu'aux notifications applicatives ;
          // les sollicitations restent toujours visibles quel que soit le filtre d'état.
          if (
            state === "unread" &&
            isNotificationCenterAppNotification(item) &&
            item.read
          ) {
            return false
          }
          return true
        })
        .sort(sortNotificationCenterItems)
    },

    unreadCount(): number {
      return this.visibleItems.filter((item) => {
        // Sollicitations : toujours comptées (pas de statut lu/non-lu).
        if (isNotificationCenterSolicitation(item)) {
          return true
        }
        return isNotificationCenterAppNotification(item) && !item.read
      }).length
    },

    establishmentOptions(): { id: number; name: string }[] {
      const map = new Map<number, string>()
      this.visibleItems.forEach((item) => {
        map.set(item.establishmentId, item.establishmentName)
      })
      return Array.from(map.entries())
        .map(([id, name]) => ({ id, name }))
        .sort((a, b) => a.name.localeCompare(b.name, "fr"))
    },
  },

  actions: {
    async load(force = false) {
      if (this.loading) {
        return
      }
      if (this.loaded && !force) {
        return
      }

      this.loading = true

      try {
        await ensureEstablishmentsLoaded(force)
        await ensureDestinationProvidersLoaded(force)

        const accountStore = useAccountStore()
        const authStore = useAuthenticationStore()
        const spaceStore = useSpaceStore()
        const establishmentNames = buildEstablishmentNameMap()

        if (spaceStore.isDialogue && spaceStore.tourismInformationCenters.length === 0) {
          await spaceStore.getTourismInformationCenters()
        }

        const dialogueTicIds = spaceStore.isDialogue
          ? spaceStore.tourismInformationCenters.map((tic) => tic.id)
          : undefined

        const [notificationsResponse, solicitationItems] = await Promise.all([
          NotificationDataService.getNotifications(authStore.currentHeaders),
          fetchSolicitationCenterItems({
            establishments: accountStore.establishments,
            destinationProviders: spaceStore.destinationProviders,
            ticIds: dialogueTicIds,
            isDialogue: spaceStore.isDialogue,
          }),
        ])

        const notifications = (notificationsResponse.data.data ?? []) as INotification[]
        const appItems = mapAppNotificationsToCenterItems(notifications, establishmentNames)

        this.items = [...solicitationItems, ...appItems]
        this.loaded = true
      } finally {
        this.loading = false
      }
    },

    resetFilters() {
      this.filters = defaultFilters()
    },

    setFilter<K extends keyof INotificationCenterFilters>(key: K, value: INotificationCenterFilters[K]) {
      this.filters[key] = value
    },

    async markAsRead(id: string) {
      const item = this.items.find((entry) => entry.id === id)
      if (!item || !isNotificationCenterAppNotification(item) || item.read) {
        return
      }

      item.read = true

      try {
        await NotificationDataService.markNotificationAsRead(id)
      } catch {
        item.read = false
      }
    },

    async archiveItem(id: string) {
      const item = this.items.find((entry) => entry.id === id)
      if (!item || isNotificationCenterSolicitation(item)) {
        return
      }

      await NotificationDataService.deleteNotification(id)
      this.items = this.items.filter((entry) => entry.id !== id)
    },

    clear() {
      this.items = []
      this.filters = defaultFilters()
      this.loaded = false
      this.loading = false
    },
  },
})
